import React from 'react';
import {
  Box, Typography, Stack, FormControl, Select, MenuItem,
  FormControlLabel, Switch, Divider,
} from '@mui/material';

const navy = '#1e3a5f';
const selectSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

const SwitchRow = ({ label, hint, checked, onChange }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Box>
      <Typography sx={{ fontSize: '0.875rem', color: '#111827', fontWeight: 500 }}>{label}</Typography>
      {hint && <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>{hint}</Typography>}
    </Box>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      size="small"
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': { color: navy },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: navy },
      }}
    />
  </Stack>
);

const SectionBox = ({ title, children }) => (
  <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 2 }}>
    <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem', mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const DISPLAY_MODES = [
  { value: 'section',     label: 'סקשן מעל הציר',     desc: 'רצועה אופקית קומפקטית מעל ציר הזמנים' },
  { value: 'sidebar',     label: 'סרגל צד',            desc: 'פאנל אנכי בצד ציר הזמנים' },
  { value: 'annotations', label: 'הערות על הציר',       desc: 'פריטים משולבים ישירות בציר לצד הזמנים' },
  { value: 'combined',    label: 'משולב',               desc: 'הערות על הציר + סרגל צד' },
];

/**
 * ZmanimInyaneiStep
 * שלב הגדרות ענייני היום בתוך הוויזרד של ZmanimWidget.
 * מאפשר הפעלה/כיבוי, בחירת מצב תצוגה, בחירת עדה, ובחירת קטגוריות.
 */
const ZmanimInyaneiStep = ({ formData, onChange }) => {
  const showInyaneiHayom = formData?.showInyaneiHayom ?? false;
  const inyaneiDisplayMode = formData?.inyaneiDisplayMode ?? 'section';
  const inyaneiEdah = formData?.inyaneiEdah ?? 'ashkenaz';

  // Category toggles (stored in formData.inyaneiCategories)
  const cats = formData?.inyaneiCategories || {};
  const catVal = (key, def = true) => cats[key] ?? def;

  const setCat = (key, val) => {
    onChange({ inyaneiCategories: { ...cats, [key]: val } });
  };

  return (
    <Stack spacing={2} sx={{ direction: 'rtl' }}>
      {/* Master toggle */}
      <SectionBox title="ענייני היום בציר הזמנים">
        <SwitchRow
          label="הצג ענייני היום"
          hint="מידע הלכתי-תפילתי (תחנון, הלל, גשם/טל, מנהגים ועוד) על ציר הזמנים"
          checked={showInyaneiHayom}
          onChange={(v) => onChange({ showInyaneiHayom: v })}
        />
      </SectionBox>

      {showInyaneiHayom && (
        <>
          {/* Display mode */}
          <SectionBox title="מצב תצוגה">
            <FormControl fullWidth size="small" sx={selectSx}>
              <Select
                value={inyaneiDisplayMode}
                onChange={(e) => onChange({ inyaneiDisplayMode: e.target.value })}
                sx={{ fontSize: '0.875rem' }}
              >
                {DISPLAY_MODES.map((mode) => (
                  <MenuItem key={mode.value} value={mode.value}>
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{mode.label}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>{mode.desc}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </SectionBox>

          {/* Edah */}
          <SectionBox title="נוסח / עדה">
            <FormControl fullWidth size="small" sx={selectSx}>
              <Select
                value={inyaneiEdah}
                onChange={(e) => onChange({ inyaneiEdah: e.target.value })}
                sx={{ fontSize: '0.875rem' }}
              >
                <MenuItem value="ashkenaz">אשכנז</MenuItem>
                <MenuItem value="sephardic">ספרד (עדות המזרח — נוסח ספרד)</MenuItem>
                <MenuItem value="mizrahi">מזרחי (עיראק, תימן, מרוקו)</MenuItem>
              </Select>
            </FormControl>
          </SectionBox>

          {/* Categories */}
          <SectionBox title="קטגוריות להצגה">
            <Stack spacing={1} divider={<Divider flexItem />}>
              <SwitchRow
                label="תחנון"
                hint="אין תחנון — עם סיבה"
                checked={catVal('showTachanun')}
                onChange={(v) => setCat('showTachanun', v)}
              />
              <SwitchRow
                label="הלל"
                hint="הלל שלם / חצי הלל"
                checked={catVal('showHallel')}
                onChange={(v) => setCat('showHallel', v)}
              />
              <SwitchRow
                label="תוספות בתפילה"
                hint="יעלה ויבוא, על הניסים, עננו"
                checked={catVal('showPrayerAdditions')}
                onChange={(v) => setCat('showPrayerAdditions', v)}
              />
              <SwitchRow
                label="גשם / טל"
                hint="משיב הרוח, מוריד הטל, ותן טל ומטר"
                checked={catVal('showSeasonalPrayers')}
                onChange={(v) => setCat('showSeasonalPrayers', v)}
              />
              <SwitchRow
                label="ימי מעבר"
                hint="התחלת/הפסקת משיב הרוח וטל ומטר"
                checked={catVal('showSeasonalTransitions')}
                onChange={(v) => setCat('showSeasonalTransitions', v)}
              />
              <SwitchRow
                label="סליחות"
                checked={catVal('showSelichot')}
                onChange={(v) => setCat('showSelichot', v)}
              />
              <SwitchRow
                label="תפילות חול"
                hint="אבינו מלכנו, ק-ל ארך אפים"
                checked={catVal('showWeekdayPrayers')}
                onChange={(v) => setCat('showWeekdayPrayers', v)}
              />
              <SwitchRow
                label="תוספות שבת"
                hint="אב הרחמים, צדקתך, שבת מברכים"
                checked={catVal('showShabbatAdditions')}
                onChange={(v) => setCat('showShabbatAdditions', v)}
              />
              <SwitchRow
                label="מוצאי שבת"
                hint="ויהי נועם, ויתן לך"
                checked={catVal('showMotzaeiShabbat')}
                onChange={(v) => setCat('showMotzaeiShabbat', v)}
              />
              <SwitchRow
                label="מנהגים עונתיים"
                hint="לדוד, פרקי אבות, ברכי נפשי, עירוב תבשילין, ספירת העומר"
                checked={catVal('showSeasonalCustoms')}
                onChange={(v) => setCat('showSeasonalCustoms', v)}
              />
              <SwitchRow
                label="ליטורגיה"
                hint="מוסף, יזכור, ספרי תורה, קידוש לבנה"
                checked={catVal('showLiturgy')}
                onChange={(v) => setCat('showLiturgy', v)}
              />
              <SwitchRow
                label="תקופות אבלות"
                hint="בין המצרים, תשעת הימים"
                checked={catVal('showMourningPeriods')}
                onChange={(v) => setCat('showMourningPeriods', v)}
              />
            </Stack>
          </SectionBox>
        </>
      )}
    </Stack>
  );
};

export default ZmanimInyaneiStep;
